import React, {useContext, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaCopy, FaPlus} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import RoleUsers from "./components/role-users.jsx";
import RolePermissionModal from "./components/role-permission-modal.jsx";
import {RoleContext} from "./context/role-context.jsx";
import {useQuery} from "@tanstack/react-query";
import roleService from "../../../../services/web/role-service.js";
import TreePermissions from "../../../../components/tree-permissions/tree-permissions.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import Alert from "../../../../components/alert/alert.jsx";

const RoleForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const {fetchData, name, setName, permissionsSelected, lanesSelected, maintenanceSelected, setPermissionsSelected, isLoadingFetch, activeUsers, refetch, protectionProgramSelected} = useContext(RoleContext);

    const [isLoading, setIsLoading] = useState(false);
    const [predefinedRole, setPredefinedRole] = useState(undefined);
    const [permissionModal, setPermissionModal] = useState(false);

    const isAdmin = fetchData?.name === "ADMIN";
    const isAlmoxarife = fetchData?.name === "ALMOXARIFE";

    const {data: permissions} = useQuery({
        queryFn: () => roleService.getPermissions(),
        queryKey: ["permissions"],
    });

    const {data: predefined} = useQuery({
        queryFn: () => roleService.predefinedRoles(id),
        queryKey: ["predefinedRoles"],
        enabled: !id
    });

    const handlePredefinedRole = (value) => {
        if (value) {
            const permissionsFunctions = JSON.parse(value.permissions);

            const permissionList = [];

            permissions?.forEach(permission => {
                const {name} = permission;
                if (permissionsFunctions.includes(name)) {
                    permissionList.push(permission);
                }
            });

            setPermissionsSelected(permissionList);
            setName(value.label);
        }

        setPredefinedRole(value)
    }

    const save = () => {
        setIsLoading(true)

        const formData = new FormData();
        formData.append("name", name);
        formData.append("maintenanceGroup", maintenanceSelected ? maintenanceSelected.value : null);
        formData.append("permissions", JSON.stringify(permissionsSelected));
        formData.append("users", JSON.stringify(activeUsers));
        formData.append("lanes", JSON.stringify(lanesSelected));
        formData.append("protectionPrograms", JSON.stringify(protectionProgramSelected));

        let response;
        if (id) {
            formData.append("_method", "PUT");
            response = roleService.edit(id, formData);
        } else response = roleService.create(formData);

        response
            .then(({data}) => {
                projectUtils.success(true);

                if (id) {
                    window.top.location.reload();
                    return;
                }

                const urlDev = `/role/form/${data.id}`;
                navigate(urlDev);
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            }).finally(() => setIsLoading(false))
    }

    const deleteRole = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir a função "${name ?? id}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    setIsLoading(true)

                    roleService.delete(id)
                        .then(() => {
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/role");
                        })
                        .catch((error) => {
                            console.error(error);
                            let message = 'Não foi possível excluir.';
                            if (error.response.status !== 500) {
                                message = error.response.data.message;
                            }

                            projectUtils.swalErrorMessage('', message, 'Ok')
                        }).finally(() => setIsLoading(false))
                }
            }
        );
    }

    return (
        <>
            <Template isLoading={isLoadingFetch || isLoading}>
                <Template.Header>
                    <Template.Title>Função</Template.Title>

                    {
                        id &&
                        <div className={"flex gap-2"}>
                            <Button onClick={() => navigate("/role/form?cp=" + id, true)}>
                                <FaCopy /> Copiar função
                            </Button>

                            <Button target={"default"} onClick={() => navigate("/role/form")}>
                                <FaPlus /> Adicionar função
                            </Button>
                        </div>
                    }
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Body>
                            <InputForm
                                id={"name"}
                                value={name}
                                label={"Nome *"}
                                onChange={({target}) => setName(target.value)}
                                disabled={isAdmin || isAlmoxarife}
                            />
                        </Card.Body>
                    </Card>

                    <section className={"grid grid-cols-2 gap-3 h-[calc(100vh-195px)]"}>
                        <Card>
                            <Card.Header>
                                <Card.Title>Permissões selecionadas</Card.Title>
                            </Card.Header>
                            <Card.Body className={"h-[calc(100vh-282px)] overflow-auto"}>
                                <TreePermissions
                                    permissionsSelected={permissionsSelected}
                                    lanesSelected={lanesSelected}
                                    maintenanceSelected={maintenanceSelected}
                                    protectionProgramSelected={protectionProgramSelected}
                                />
                            </Card.Body>
                            <Card.Footer className={"flex !justify-end gap-2"}>
                                {
                                    !id &&
                                    <SelectComponent
                                        id="roles"
                                        classNameWrapper={"w-4/12"}
                                        value={predefinedRole}
                                        options={predefined ?? []}
                                        label={"Funções predefinidas"}
                                        onChange={handlePredefinedRole}
                                    />
                                }

                                <Button disabled={isAdmin || isAlmoxarife} onClick={() => setPermissionModal(true)}>
                                    Editar permissões
                                </Button>
                            </Card.Footer>
                        </Card>

                        <RoleUsers />
                    </section>

                    <Card>
                        <Card.Body className={"flex justify-end gap-2"}>
                            {
                                isAlmoxarife &&
                                <Alert className={"!text-[13px] !px-4 !py-2 !h-[32px]"} target={"warning"}>
                                    A função ALMOXARIFE não pode ser excluída.
                                </Alert>
                            }

                            {
                                isAdmin &&
                                <Alert className={"!text-[13px] !px-4 !py-2 !h-[32px]"} target={"warning"}>
                                    A função ADMIN não pode ser excluída.
                                </Alert>
                            }

                            {
                                id &&
                                <Button target={"danger"} onClick={deleteRole} disabled={isAdmin || isAlmoxarife} actionFeedbackType='delete'>

                                    Excluir
                                </Button>
                            }
                            <Button onClick={save} actionFeedbackType='save'>

                                Salvar
                            </Button>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <RolePermissionModal
                permissions={permissions}
                open={permissionModal}
                setOpen={setPermissionModal}
            />
        </>
    );
};

export default RoleForm;